import { Directive, FactoryProvider, Injectable, OnDestroy, Optional, SkipSelf } from '@angular/core';
import { SimDateAdapter } from '@ngx-simple/core/datetime';
import { SafeAny } from '@ngx-simple/core/types';
import { Observable, Subject } from 'rxjs';

/** 表示日期范围的类 */
export class DateRange<D> {
  constructor(
    /** 范围的开始日期 */
    readonly start: D | null,
    /** 范围的结束日期 */
    readonly end: D | null
  ) {}
}

/**
 * 如果传入了 `DateRange`，则有条件地选择日期类型
 */
export type ExtractDateTypeFromSelection<T> = T extends DateRange<infer D> ? D : NonNullable<T>;

/** 日期选择模型的选择发生更改时发出的事件 */
export interface DateSelectionModelChange<S> {
  /** 选择的新值 */
  selection: S;

  /** 触发更改的对象 */
  source: unknown;
}

/** 包含日期选择的选择模型 */
@Directive()
export abstract class SimDateSelectionModel<S, D = ExtractDateTypeFromSelection<S>> implements OnDestroy {
  private _selectionChanged = new Subject<DateSelectionModelChange<S>>();

  /** 选择发生变化时发出事件 */
  selectionChanged: Observable<DateSelectionModelChange<S>> = this._selectionChanged;

  protected constructor(
    /** 当前选择 */
    readonly selection: SafeAny,
    protected _adapter: SimDateAdapter<D>
  ) {
    this.selection = selection;
  }

  /**
   * 更新当前选择的模型
   * @param value 应该分配的新选择
   * @param source 触发选择更改的对象
   */
  updateSelection(value: S, source: unknown) {
    (this as { selection: S }).selection = value;
    this._selectionChanged.next({ selection: value, source });
  }

  ngOnDestroy() {
    this._selectionChanged.complete();
  }

  protected _isValidDateInstance(date: D): boolean {
    return this._adapter.isDateInstance(date) && this._adapter.isValid(date);
  }

  /** 向当前选择添加日期 */
  abstract add(date: D | null): void;

  /** 检查当前选择是否有效 */
  abstract isValid(): boolean;

  /** 检查当前选择是否完成 */
  abstract isComplete(): boolean;
}

/**  包含单个日期的选择模型 */
@Injectable()
export class SimSingleDateSelectionModel<D> extends SimDateSelectionModel<D | null, D> {
  constructor(adapter: SimDateAdapter<D>) {
    super(null, adapter);
  }

  /**
   * 向当前选择添加日期。在单个日期选择的情况下，添加的date简单地覆盖了之前的选择
   */
  add(date: D | null) {
    super.updateSelection(date, this);
  }

  /** 检查当前选择是否有效 */
  isValid(): boolean {
    return this.selection != null && this._isValidDateInstance(this.selection);
  }

  /**
   * 检查当前选择是否完成。在单个日期选择的情况下，如果当前选择不为空，则此为真。
   */
  isComplete() {
    return this.selection != null;
  }
}

/**  包含日期范围的选择模型 */
@Injectable()
export class SimRangeDateSelectionModel<D> extends SimDateSelectionModel<DateRange<D>, D> {
  constructor(adapter: SimDateAdapter<D>) {
    super(new DateRange<D>(null, null), adapter);
  }

  /**
   * 向当前选择添加日期。在选择日期范围时，添加的date将填充范围中的下一个“null”值。
   * 如果开始和结束都已经有一个日期，选择被重置，使给定的日期是新的“开始”和“结束”是空的。
   */
  add(date: D | null): void {
    let { start, end } = this.selection;

    if (start == null) {
      start = date;
    } else if (end == null) {
      end = date;
    } else {
      start = date;
      end = null;
    }

    super.updateSelection(new DateRange<D>(start, end), this);
  }

  /** 检查当前选择是否有效 */
  isValid(): boolean {
    const { start, end } = this.selection;

    // 空范围有效.
    if (start == null && end == null) {
      return true;
    }

    // 只有两个日期都有效并且开始日期在结束日期之前，完整范围才有效。
    if (start != null && end != null) {
      return this._isValidDateInstance(start) && this._isValidDateInstance(end) && this._adapter.compareDate(start, end) <= 0;
    }

    // 如果开始/结束有效，则部分范围有效
    return (start == null || this._isValidDateInstance(start)) && (end == null || this._isValidDateInstance(end));
  }

  /**
   * 检查当前选择是否完成。在日期范围选择的情况下，如果当前选择的`start`和`end`是非空的，则此为真。
   */
  isComplete(): boolean {
    return this.selection.start != null && this.selection.end != null;
  }
}

export function SIM_SINGLE_DATE_SELECTION_MODEL_FACTORY(parent: SimSingleDateSelectionModel<unknown>, adapter: SimDateAdapter<unknown>) {
  return parent || new SimSingleDateSelectionModel(adapter);
}

/** 用于为组件提供单个选择模型 */
export const SIM_SINGLE_DATE_SELECTION_MODEL_PROVIDER: FactoryProvider = {
  provide: SimDateSelectionModel,
  deps: [[new Optional(), new SkipSelf(), SimDateSelectionModel], SimDateAdapter],
  useFactory: SIM_SINGLE_DATE_SELECTION_MODEL_FACTORY
};

export function SIM_RANGE_DATE_SELECTION_MODEL_FACTORY(parent: SimSingleDateSelectionModel<unknown>, adapter: SimDateAdapter<unknown>) {
  return parent || new SimRangeDateSelectionModel(adapter);
}

/** Used to provide a range selection model to a component. */
export const SIM_RANGE_DATE_SELECTION_MODEL_PROVIDER: FactoryProvider = {
  provide: SimDateSelectionModel,
  deps: [[new Optional(), new SkipSelf(), SimDateSelectionModel], SimDateAdapter],
  useFactory: SIM_RANGE_DATE_SELECTION_MODEL_FACTORY
};
